import {
  ApiErrorBody,
  ApiSuccess,
  AgentExecutionResult,
  AgentExecutionSummary,
  AgentChangeSet,
  AgentChangeExecution,
  AgentJob,
  CurrentMember,
  CursorPage,
  Message,
  MessageCreateInput,
  Mention,
  ApprovalDecisionValue,
  ApprovalRequest,
  ApprovalStatus,
  ApprovalType,
  ReadCursor,
  Room,
  RoomCreateInput,
  GateEvaluation,
  Opportunity,
  OpportunityStage,
  Project,
  ProjectRevision,
  ProjectSnapshot,
  ProjectStage,
  WorkItem,
  WorkItemDependency,
  WorkItemPriority,
  WorkItemStatus,
  WorkItemType,
  TaskRequestCreateInput,
  MailConnection,
  MailAccount,
  MailFolder,
  MailThread,
  MailThreadDetail,
  MailMessage,
  MailRoutingRule,
  MailRoutingConflict,
  MailDraft,
  MailDraftRevision,
  MailSyncRun,
  MailOAuthStatus,
  CodexPilotStatus,
  WorkflowRun,
  AgentHandoff,
  Meeting,
  MeetingContribution,
  MeetingMinutes,
  DailyAgentReport,
  CollaborationActionCenter,
  ReleaseCandidate,
  DeliveryPackage,
  DeliveryIntent,
  DeliveryCenterData,
  OutboundMailAttempt,
  OutboundMailIntent,
  SalesContact,
  SalesEngagement,
  SalesReview,
  SalesFollowUp,
} from "../types";

const API_PREFIX = "/api/neuralchat/v1";

export class NeuralChatApiError extends Error {
  code: string;
  status: number;
  retryable: boolean;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "NeuralChatApiError";
    this.code = code;
    this.status = status;
    this.retryable = status >= 500 || status === 408 || status === 429;
  }
}

let csrfToken: string | null = null;

const randomId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `neuralchat-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const parseJson = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const invalidResponse = () => new NeuralChatApiError("NEURALCHAT_RESPONSE_INVALID", "서버 응답 형식을 확인할 수 없습니다.", 502);
const responseData = (payload: unknown): unknown => {
  if (!isRecord(payload) || !("data" in payload)) throw invalidResponse();
  return payload.data;
};
const requiredString = (value: unknown): string => {
  if (typeof value !== "string" || !value) throw invalidResponse();
  return value;
};
const memberResponse = (value: unknown) => {
  if (!isRecord(value)) throw invalidResponse();
  requiredString(value.id); requiredString(value.display_name); requiredString(value.kind); requiredString(value.role);
  return value as unknown as CurrentMember;
};
const snapshotResponse = (value: unknown): ProjectSnapshot => {
  if (!isRecord(value)) throw invalidResponse();
  const arrays = ["deliverables", "exclusions", "acceptance_criteria"];
  arrays.forEach((key) => { if (!Array.isArray(value[key]) || (value[key] as unknown[]).some((item) => typeof item !== "string")) throw invalidResponse(); });
  const price = value.price;
  const schedule = value.schedule;
  if (!isRecord(price) || (typeof price.amount !== "string" && price.amount !== null) || typeof price.currency !== "string" || !isRecord(schedule)) throw invalidResponse();
  return value as unknown as ProjectSnapshot;
};
const opportunityResponse = (value: unknown): Opportunity => {
  if (!isRecord(value)) throw invalidResponse();
  requiredString(value.id); requiredString(value.room_id); requiredString(value.title); requiredString(value.stage); memberResponse(value.owner);
  if (typeof value.version !== "number") throw invalidResponse();
  if (value.room_is_archived !== undefined && typeof value.room_is_archived !== "boolean") throw invalidResponse();
  return value as unknown as Opportunity;
};
const revisionResponse = (value: unknown): ProjectRevision => {
  if (!isRecord(value)) throw invalidResponse();
  requiredString(value.id); requiredString(value.project_id); requiredString(value.snapshot_hash);
  if (typeof value.revision !== "number") throw invalidResponse();
  if (value.snapshot !== undefined) snapshotResponse(value.snapshot);
  return value as unknown as ProjectRevision;
};
const projectResponse = (value: unknown): Project => {
  if (!isRecord(value)) throw invalidResponse();
  requiredString(value.id); requiredString(value.room_id); requiredString(value.code); requiredString(value.current_stage);
  if (typeof value.version !== "number" || typeof value.current_revision_number !== "number") throw invalidResponse();
  if (value.room_is_archived !== undefined && typeof value.room_is_archived !== "boolean") throw invalidResponse();
  if (value.current_revision !== null && value.current_revision !== undefined) revisionResponse(value.current_revision);
  return value as unknown as Project;
};
const workItemResponse = (value: unknown): WorkItem => {
  if (!isRecord(value)) throw invalidResponse();
  requiredString(value.id); requiredString(value.project_id); requiredString(value.title); requiredString(value.status); memberResponse(value.assignee);
  if (typeof value.version !== "number" || !Array.isArray(value.dependencies)) throw invalidResponse();
  return value as unknown as WorkItem;
};
const changeExecutionResponse = (value: unknown): AgentChangeExecution => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.change_set_id !== "string" || typeof value.status !== "string" || typeof value.expected_version !== "number" || typeof value.command_id !== "string") throw invalidResponse();
  if (value.result !== null && value.result !== undefined && !isRecord(value.result)) throw invalidResponse();
  return value as unknown as AgentChangeExecution;
};
const gateResponse = (value: unknown): GateEvaluation => {
  if (!isRecord(value) || typeof value.allowed !== "boolean" || !Array.isArray(value.missing_codes) || !Array.isArray(value.messages)) throw invalidResponse();
  return value as unknown as GateEvaluation;
};
const approvalResponse = (value: unknown): ApprovalRequest => {
  if (!isRecord(value)) throw invalidResponse();
  requiredString(value.id); requiredString(value.project_id); requiredString(value.approval_type); requiredString(value.target_hash); requiredString(value.status);
  if (typeof value.request_version !== "number") throw invalidResponse();
  snapshotResponse(value.target_snapshot);
  return value as unknown as ApprovalRequest;
};
const mailConnectionResponse = (value: unknown): MailConnection => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.display_name !== "string" || typeof value.primary_email !== "string" || typeof value.location !== "string" || typeof value.version !== "number") throw invalidResponse();
  if (!Array.isArray(value.aliases) || !Array.isArray(value.granted_scopes)) throw invalidResponse();
  return value as unknown as MailConnection;
};
const mailAccountResponse = (value: unknown): MailAccount => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.connection_id !== "string" || typeof value.primary_email !== "string") throw invalidResponse();
  return value as unknown as MailAccount;
};
const mailFolderResponse = (value: unknown): MailFolder => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.account_id !== "string" || typeof value.display_name !== "string") throw invalidResponse();
  return value as unknown as MailFolder;
};
const mailThreadResponse = (value: unknown): MailThread => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.account_id !== "string" || typeof value.version !== "number") throw invalidResponse();
  if (value.has_attachments !== undefined && typeof value.has_attachments !== "boolean") throw invalidResponse();
  return value as unknown as MailThread;
};
const mailMessageResponse = (value: unknown): MailMessage => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.thread_id !== "string" || typeof value.body_text !== "string") throw invalidResponse();
  return value as unknown as MailMessage;
};
const mailRevisionResponse = (value: unknown): MailDraftRevision => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.draft_id !== "string" || typeof value.revision !== "number" || typeof value.canonical_hash !== "string") throw invalidResponse();
  return value as unknown as MailDraftRevision;
};
const mailDraftResponse = (value: unknown): MailDraft => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.version !== "number") throw invalidResponse();
  if (value.current_revision !== null && value.current_revision !== undefined) mailRevisionResponse(value.current_revision);
  return value as unknown as MailDraft;
};
const mailSyncResponse = (value: unknown): MailSyncRun => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.status !== "string" || typeof value.fetched_count !== "number") throw invalidResponse();
  return value as unknown as MailSyncRun;
};
const outboundAttemptResponse = (value: unknown): OutboundMailAttempt => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.intent_id !== "string" || typeof value.status !== "string") throw invalidResponse();
  return value as unknown as OutboundMailAttempt;
};
const outboundIntentResponse = (value: unknown): OutboundMailIntent => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.status !== "string" || typeof value.version !== "number" || typeof value.snapshot_hash !== "string" || !Array.isArray(value.to_addresses) || !Array.isArray(value.cc_addresses)) throw invalidResponse();
  if (value.attempts !== undefined && Array.isArray(value.attempts)) value.attempts.forEach(outboundAttemptResponse);
  return value as unknown as OutboundMailIntent;
};
const salesContactResponse = (value: unknown): SalesContact => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.verification_status !== "string") throw invalidResponse();
  return value as unknown as SalesContact;
};
const salesReviewResponse = (value: unknown): SalesReview => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.classification !== "string" || typeof value.safe_summary !== "string") throw invalidResponse();
  return value as unknown as SalesReview;
};
const salesEngagementResponse = (value: unknown): SalesEngagement => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.stage !== "string" || typeof value.version !== "number") throw invalidResponse();
  if (value.reviews !== undefined && Array.isArray(value.reviews)) value.reviews.forEach(salesReviewResponse);
  return value as unknown as SalesEngagement;
};
const salesFollowUpResponse = (value: unknown): SalesFollowUp => {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.engagement_id !== "string" || typeof value.contact_id !== "string" || typeof value.due_at !== "string" || typeof value.status !== "string" || typeof value.canonical_hash !== "string") throw invalidResponse();
  return value as unknown as SalesFollowUp;
};
const collaborationRecord = <T>(value: unknown, required: string[]): T => {
  if (!isRecord(value) || required.some((key) => typeof value[key] !== "string")) throw invalidResponse();
  return value as unknown as T;
};
const workflowResponse = (value: unknown): WorkflowRun => {
  const row = collaborationRecord<WorkflowRun>(value, ["id", "room_id", "project_id", "template", "status"]);
  if (typeof row.version !== "number" || !Array.isArray(row.steps)) throw invalidResponse();
  return row;
};
const handoffResponse = (value: unknown): AgentHandoff => collaborationRecord<AgentHandoff>(value, ["id", "room_id", "project_id", "source_agent_id", "target_agent_id", "status"]);
const meetingResponse = (value: unknown): Meeting => {
  const row = collaborationRecord<Meeting>(value, ["id", "room_id", "title", "purpose", "status"]);
  if (typeof row.version !== "number" || !Array.isArray(row.participants)) throw invalidResponse();
  return row;
};
const dailyReportResponse = (value: unknown): DailyAgentReport => collaborationRecord<DailyAgentReport>(value, ["id", "agent_id", "report_date", "canonical_hash"]);
const releaseCandidateResponse = (value: unknown): ReleaseCandidate => {
  const row = collaborationRecord<ReleaseCandidate>(value, ["id", "room_id", "project_id", "candidate_type", "title", "status", "snapshot_hash"]);
  if (typeof row.version !== "number" || typeof row.revision !== "number" || !Array.isArray(row.work_item_ids) || !isRecord(row.customer_delivery)) throw invalidResponse();
  return row;
};
const deliveryPackageResponse = (value: unknown): DeliveryPackage => {
  const row = collaborationRecord<DeliveryPackage>(value, ["id", "room_id", "project_id", "candidate_id", "status", "manifest_hash"]);
  if (typeof row.version !== "number" || !isRecord(row.customer_delivery)) throw invalidResponse();
  return row;
};
const deliveryIntentResponse = (value: unknown): DeliveryIntent => collaborationRecord<DeliveryIntent>(value, ["id", "project_id", "candidate_id", "package_id", "status", "canonical_hash"]);
const listData = <T>(payload: unknown, parser: (value: unknown) => T): T[] => {
  const data = responseData(payload);
  if (!Array.isArray(data)) throw invalidResponse();
  return data.map(parser);
};

const request = async <T>(
  path: string,
  options: RequestInit = {},
  stateChanging = false,
): Promise<T> => {
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  headers.set("Cache-Control", "no-store");
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (stateChanging) {
    const token = await getCsrfToken(options.signal ?? undefined);
    headers.set("X-CSRFToken", token);
  }

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      ...options,
      credentials: "include",
      cache: "no-store",
      headers,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new NeuralChatApiError(
      "NEURALCHAT_NETWORK_ERROR",
      "네트워크 연결을 확인해 주세요.",
      0,
    );
  }

  const payload = await parseJson(response);
  if (!response.ok) {
    const body = payload as Partial<ApiErrorBody> | null;
    const code = body?.error?.code || (response.status === 401 ? "NEURALCHAT_AUTH_REQUIRED" : "NEURALCHAT_REQUEST_FAILED");
    const message = body?.error?.message || "요청을 처리하지 못했습니다.";
    if (response.status === 401) {
      csrfToken = null;
    }
    throw new NeuralChatApiError(code, message, response.status);
  }
  return payload as T;
};

export const getCsrfToken = async (signal?: AbortSignal): Promise<string> => {
  if (csrfToken) return csrfToken;
  const response = await request<ApiSuccess<{ csrf_token: string }>>(
    "/auth/csrf/",
    { method: "GET", signal },
    false,
  );
  csrfToken = response.data.csrf_token;
  return csrfToken;
};

export const resetCsrfToken = () => {
  csrfToken = null;
};

export const api = {
  async login(identifier: string, password: string, signal?: AbortSignal) {
    const response = await request<ApiSuccess<{ user: CurrentMember }>>(
      "/auth/login/",
      {
        method: "POST",
        body: JSON.stringify({ username: identifier, password }),
        signal,
      },
      true,
    );
    // Django rotates the CSRF secret during login.
    resetCsrfToken();
    return response.data.user;
  },

  async me(signal?: AbortSignal) {
    const response = await request<ApiSuccess<{ user: CurrentMember }>>(
      "/auth/me/",
      { method: "GET", signal },
    );
    return response.data.user;
  },

  async logout(signal?: AbortSignal) {
    try {
      await request<ApiSuccess<{ logged_out: boolean }>>(
        "/auth/logout/",
        { method: "POST", body: JSON.stringify({}), signal },
        true,
      );
    } finally {
      resetCsrfToken();
    }
  },

  async listRooms(signal?: AbortSignal) {
    const response = await request<ApiSuccess<Room[]>>("/rooms/", {
      method: "GET",
      signal,
    });
    return response.data;
  },

  async createRoom(input: RoomCreateInput, signal?: AbortSignal) {
    const response = await request<ApiSuccess<Room>>(
      "/rooms/",
      { method: "POST", body: JSON.stringify(input), signal },
      true,
    );
    return response.data;
  },

  async getRoom(roomId: string, signal?: AbortSignal) {
    const response = await request<ApiSuccess<Room>>(
      `/rooms/${encodeURIComponent(roomId)}/`,
      { method: "GET", signal },
    );
    return response.data;
  },

  async listMessages(
    roomId: string,
    before?: string | null,
    limit = 50,
    signal?: AbortSignal,
  ): Promise<CursorPage<Message>> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (before) params.set("before", before);
    const response = await request<ApiSuccess<Message[]>>(
      `/rooms/${encodeURIComponent(roomId)}/messages/?${params.toString()}`,
      { method: "GET", signal },
    );
    return {
      rows: response.data,
      nextCursor: typeof response.meta.next_cursor === "string" ? response.meta.next_cursor : null,
      limit: typeof response.meta.limit === "number" ? response.meta.limit : limit,
    };
  },

  async postMessage(input: MessageCreateInput, roomId: string, signal?: AbortSignal) {
    const response = await request<ApiSuccess<Message>>(
      `/rooms/${encodeURIComponent(roomId)}/messages/`,
      {
        method: "POST",
        body: JSON.stringify(input),
        headers: { "Idempotency-Key": input.client_message_id },
        signal,
      },
      true,
    );
    return {
      message: response.data,
      replayed: response.meta.idempotent_replay === true,
    };
  },

  async createTaskRequest(input: TaskRequestCreateInput, roomId: string, signal?: AbortSignal) {
    const response = await request<ApiSuccess<{ message: Message; agent_job: AgentJob }>>(
      `/rooms/${encodeURIComponent(roomId)}/task-requests/`,
      {
        method: "POST",
        body: JSON.stringify(input),
        headers: { "Idempotency-Key": input.client_request_id },
        signal,
      },
      true,
    );
    return {
      message: response.data.message,
      job: response.data.agent_job,
      replayed: response.meta.idempotent_replay === true,
    };
  },

  async listAgentJobs(roomId: string, signal?: AbortSignal): Promise<AgentJob[]> {
    const response = await request<ApiSuccess<AgentJob[]>>(
      `/rooms/${encodeURIComponent(roomId)}/task-requests/`,
      { method: "GET", signal },
    );
    return response.data;
  },

  async getAgentJob(roomId: string, jobId: string, signal?: AbortSignal): Promise<AgentJob> {
    const response = await request<ApiSuccess<AgentJob>>(
      `/rooms/${encodeURIComponent(roomId)}/task-requests/${encodeURIComponent(jobId)}/`,
      { method: "GET", signal },
    );
    return response.data;
  },

  async getAgentExecutionResult(jobId: string, executionId: string, signal?: AbortSignal): Promise<AgentExecutionResult> {
    const response = await request<ApiSuccess<AgentExecutionResult>>(
      `/agent-jobs/${encodeURIComponent(jobId)}/executions/${encodeURIComponent(executionId)}/result/`,
      { method: "GET", signal },
    );
    return response.data;
  },

  async getCodexPilot(roomId: string, signal?: AbortSignal): Promise<CodexPilotStatus> {
    const payload = await request<unknown>(`/rooms/${encodeURIComponent(roomId)}/codex-pilot/`, { method: "GET", signal });
    const value = responseData(payload);
    if (!isRecord(value) || typeof value.ready !== "boolean" || typeof value.enabled !== "boolean" || typeof value.active !== "boolean" || typeof value.control_version !== "number" || typeof value.max_seconds !== "number") throw invalidResponse();
    return value as unknown as CodexPilotStatus;
  },

  async startCodexPilot(roomId: string, input: { command_id: string; confirmations: { non_sensitive: true; real_request: true; read_only: true } }, signal?: AbortSignal) {
    const payload = await request<ApiSuccess<{ job_id: string; execution: AgentExecutionSummary }>>(
      `/rooms/${encodeURIComponent(roomId)}/codex-pilot/`,
      { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.command_id }, signal },
      true,
    );
    return { ...payload.data, replayed: payload.meta.idempotent_replay === true };
  },

  async cancelAgentExecution(jobId: string, executionId: string, input: { command_id: string; reason: string }, signal?: AbortSignal) {
    const payload = await request<ApiSuccess<{ execution: AgentExecutionSummary }>>(
      `/agent-jobs/${encodeURIComponent(jobId)}/executions/${encodeURIComponent(executionId)}/cancel/`,
      { method: "POST", body: JSON.stringify(input), signal },
      true,
    );
    return { ...payload.data, replayed: payload.meta.idempotent_replay === true };
  },

  async listAgentExecutions(jobId: string, signal?: AbortSignal): Promise<AgentExecutionSummary[]> {
    const response = await request<ApiSuccess<AgentExecutionSummary[]>>(`/agent-jobs/${encodeURIComponent(jobId)}/executions/`, { method: "GET", signal });
    return response.data;
  },

  async unreadMentions(signal?: AbortSignal) {
    const response = await request<ApiSuccess<Mention[]>>("/mentions/unread/", {
      method: "GET",
      signal,
    });
    return response.data;
  },

  async markRead(roomId: string, messageId: string, signal?: AbortSignal) {
    const response = await request<ApiSuccess<ReadCursor>>(
      `/rooms/${encodeURIComponent(roomId)}/read/`,
      {
        method: "POST",
        body: JSON.stringify({ last_read_message_id: messageId }),
        signal,
      },
      true,
    );
    return { cursor: response.data, changed: response.meta.changed === true };
  },

  async listOpportunities(roomId?: string, signal?: AbortSignal): Promise<Opportunity[]> {
    const query = roomId ? `?room_id=${encodeURIComponent(roomId)}` : "";
    const payload = await request<unknown>(`/opportunities/${query}`, { method: "GET", signal });
    return listData(payload, opportunityResponse);
  },

  async getOpportunity(opportunityId: string, signal?: AbortSignal): Promise<Opportunity> {
    const payload = await request<unknown>(`/opportunities/${encodeURIComponent(opportunityId)}/`, { method: "GET", signal });
    return opportunityResponse(responseData(payload));
  },

  async createOpportunity(input: { command_id: string; room_id: string; title: string; summary: string; customer_label: string; owner_member_id: string }, signal?: AbortSignal): Promise<{ value: Opportunity; replayed: boolean }> {
    const payload = await request<unknown>("/opportunities/", { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: opportunityResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async transitionOpportunity(opportunityId: string, input: { command_id: string; expected_version: number; to_stage: OpportunityStage; reason: string }, signal?: AbortSignal): Promise<{ value: Opportunity; replayed: boolean }> {
    const payload = await request<unknown>(`/opportunities/${encodeURIComponent(opportunityId)}/transition/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: opportunityResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async convertOpportunity(opportunityId: string, input: { command_id: string; expected_version: number; project_code: string; reason: string }, signal?: AbortSignal): Promise<{ value: Project; replayed: boolean }> {
    const payload = await request<unknown>(`/opportunities/${encodeURIComponent(opportunityId)}/convert/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: projectResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async listProjects(roomId?: string, signal?: AbortSignal): Promise<Project[]> {
    const query = roomId ? `?room_id=${encodeURIComponent(roomId)}` : "";
    const payload = await request<unknown>(`/projects/${query}`, { method: "GET", signal });
    return listData(payload, projectResponse);
  },

  async getProject(projectId: string, signal?: AbortSignal): Promise<Project> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/`, { method: "GET", signal });
    return projectResponse(responseData(payload));
  },

  async listProjectRevisions(projectId: string, signal?: AbortSignal): Promise<ProjectRevision[]> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/revisions/`, { method: "GET", signal });
    return listData(payload, revisionResponse);
  },

  async getProjectRevision(projectId: string, revisionId: string, signal?: AbortSignal): Promise<ProjectRevision> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/revisions/${encodeURIComponent(revisionId)}/`, { method: "GET", signal });
    return revisionResponse(responseData(payload));
  },

  async createProjectRevision(projectId: string, input: { command_id: string; expected_revision: number; snapshot: ProjectSnapshot; reason?: string }, signal?: AbortSignal): Promise<{ value: ProjectRevision; replayed: boolean }> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/revisions/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: revisionResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async transitionProject(projectId: string, input: { command_id: string; expected_version: number; to_stage: ProjectStage; reason: string }, signal?: AbortSignal): Promise<{ value: Project; replayed: boolean }> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/transition/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: projectResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async getProjectGate(projectId: string, gate?: ProjectStage, signal?: AbortSignal): Promise<GateEvaluation> {
    const query = gate ? `?gate=${encodeURIComponent(gate)}` : "";
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/gate/${query}`, { method: "GET", signal });
    return gateResponse(responseData(payload));
  },

  async listWorkItems(projectId: string, signal?: AbortSignal): Promise<WorkItem[]> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/work-items/`, { method: "GET", signal });
    return listData(payload, workItemResponse);
  },

  async createWorkItem(projectId: string, input: { command_id: string; title: string; description: string; work_type: WorkItemType; assignee_id: string; priority: WorkItemPriority; expected_output: string }, signal?: AbortSignal): Promise<{ value: WorkItem; replayed: boolean }> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/work-items/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: workItemResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async transitionWorkItem(projectId: string, workItemId: string, input: { command_id: string; expected_version: number; to_status: WorkItemStatus; reason: string }, signal?: AbortSignal): Promise<{ value: WorkItem; replayed: boolean }> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/work-items/${encodeURIComponent(workItemId)}/transition/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: workItemResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async updateWorkItemAssignee(projectId: string, workItemId: string, input: { command_id: string; expected_version: number; assignee_id: string; reason: string }, signal?: AbortSignal): Promise<{ value: WorkItem; replayed: boolean }> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/work-items/${encodeURIComponent(workItemId)}/assignee/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: workItemResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async addWorkItemDependency(projectId: string, workItemId: string, dependsOnId: string, commandId: string, signal?: AbortSignal): Promise<{ value: WorkItemDependency; replayed: boolean }> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/work-items/${encodeURIComponent(workItemId)}/dependencies/`, { method: "POST", body: JSON.stringify({ command_id: commandId, depends_on_id: dependsOnId }), signal }, true);
    const value = responseData(payload);
    if (!isRecord(value) || typeof value.id !== "string" || typeof value.work_item_id !== "string" || typeof value.depends_on_id !== "string") throw invalidResponse();
    return { value: value as unknown as WorkItemDependency, replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async removeWorkItemDependency(projectId: string, dependencyId: string, commandId: string, reason: string, signal?: AbortSignal): Promise<{ replayed: boolean }> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/dependencies/${encodeURIComponent(dependencyId)}/remove/`, { method: "POST", body: JSON.stringify({ command_id: commandId, reason }), signal }, true);
    if (!isRecord(responseData(payload))) throw invalidResponse();
    return { replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async listWorkItemDependencies(projectId: string, workItemId: string, signal?: AbortSignal): Promise<WorkItemDependency[]> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/work-items/${encodeURIComponent(workItemId)}/dependencies/`, { method: "GET", signal });
    const rows = listData(payload, (value) => {
      if (!isRecord(value) || typeof value.id !== "string" || typeof value.depends_on_id !== "string") throw invalidResponse();
      return { id: value.id, work_item_id: workItemId, depends_on_id: value.depends_on_id };
    });
    return rows;
  },

  async listApprovals(status?: ApprovalStatus | "ALL", signal?: AbortSignal): Promise<ApprovalRequest[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : "";
    const payload = await request<unknown>(`/approvals/inbox/${query}`, { method: "GET", signal });
    return listData(payload, approvalResponse);
  },

  async listProjectApprovals(projectId: string, signal?: AbortSignal): Promise<ApprovalRequest[]> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/approvals/`, { method: "GET", signal });
    return listData(payload, approvalResponse);
  },

  async listPatchProposals(projectId: string, signal?: AbortSignal): Promise<AgentChangeSet[]> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/patch-proposals/`, { method: "GET", signal });
    return listData(payload, (value) => value as AgentChangeSet);
  },

  async startPatchProposal(projectId: string, input: { room_id: string; work_item_id: string; command_id: string; repository_key: string; base_commit_sha: string; relative_path?: string; fake_replacement?: string }, signal?: AbortSignal) {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/patch-proposals/`, { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.command_id }, signal }, true);
    return responseData(payload) as { job_id: string; execution_id: string; status: string };
  },

  async getPatchProposal(changeSetId: string, signal?: AbortSignal): Promise<AgentChangeSet> {
    const payload = await request<unknown>(`/patch-proposals/${encodeURIComponent(changeSetId)}/`, { method: "GET", signal });
    return responseData(payload) as AgentChangeSet;
  },

  async decidePatchProposal(changeSetId: string, input: { command_id: string; decision: "APPROVED" | "REJECTED"; reason: string; expected_version: number; patch_sha256: string; base_commit_sha: string }, signal?: AbortSignal): Promise<AgentChangeSet> {
    const payload = await request<unknown>(`/patch-proposals/${encodeURIComponent(changeSetId)}/decision/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return (responseData(payload) as { change_set: AgentChangeSet }).change_set;
  },

  async listChangeExecutions(changeSetId: string, signal?: AbortSignal): Promise<AgentChangeExecution[]> {
    const payload = await request<unknown>(`/agent-change-sets/${encodeURIComponent(changeSetId)}/executions/`, { method: "GET", signal });
    return listData(payload, changeExecutionResponse);
  },

  async startChangeExecution(changeSetId: string, input: { command_id: string; expected_version: number; test_profile?: string }, signal?: AbortSignal): Promise<{ execution: AgentChangeExecution; replayed: boolean }> {
    const payload = await request<unknown>(`/agent-change-sets/${encodeURIComponent(changeSetId)}/executions/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload);
    if (!isRecord(value) || !isRecord(value.execution)) throw invalidResponse();
    return { execution: changeExecutionResponse(value.execution), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async cancelChangeExecution(executionId: string, input: { command_id: string; expected_version: number; reason: string }, signal?: AbortSignal): Promise<AgentChangeExecution> {
    const payload = await request<unknown>(`/agent-change-executions/${encodeURIComponent(executionId)}/cancel/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload);
    if (!isRecord(value) || !isRecord(value.execution)) throw invalidResponse();
    return changeExecutionResponse(value.execution);
  },

  async getApproval(projectId: string, approvalId: string, signal?: AbortSignal): Promise<ApprovalRequest> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/approvals/${encodeURIComponent(approvalId)}/`, { method: "GET", signal });
    return approvalResponse(responseData(payload));
  },

  async createApproval(projectId: string, input: { command_id: string; approval_type: ApprovalType; reason: string }, signal?: AbortSignal): Promise<{ value: ApprovalRequest; replayed: boolean }> {
    const payload = await request<unknown>(`/projects/${encodeURIComponent(projectId)}/approvals/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: approvalResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async decideApproval(approvalId: string, input: { command_id: string; decision: ApprovalDecisionValue; reason: string; target_hash: string; expected_request_version: number }, signal?: AbortSignal): Promise<{ value: ApprovalRequest; replayed: boolean }> {
    const payload = await request<unknown>(`/approvals/${encodeURIComponent(approvalId)}/decision/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: approvalResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async getMailOAuthStatus(signal?: AbortSignal): Promise<MailOAuthStatus> {
    const payload = await request<unknown>("/mail/oauth/status/", { method: "GET", signal });
    const value = responseData(payload);
    if (!isRecord(value) || typeof value.enabled !== "boolean" || typeof value.configured !== "boolean" || typeof value.location !== "string" || !Array.isArray(value.scopes)) throw invalidResponse();
    return value as unknown as MailOAuthStatus;
  },

  async startMailOAuth(input: { expected_mailbox_email: string; connection_display_name: string; command_id: string }, signal?: AbortSignal) {
    const payload = await request<unknown>("/mail/oauth/zoho/start/", { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload);
    if (!isRecord(value) || typeof value.attempt_id !== "string" || typeof value.authorization_url !== "string" || typeof value.expires_at !== "string") throw invalidResponse();
    return value as { attempt_id: string; authorization_url: string; expires_at: string };
  },

  async listMailConnections(signal?: AbortSignal): Promise<MailConnection[]> {
    const payload = await request<unknown>("/mail/connections/", { method: "GET", signal });
    return listData(payload, mailConnectionResponse);
  },

  async deactivateMailConnection(connectionId: string, expectedVersion: number, signal?: AbortSignal): Promise<MailConnection> {
    const payload = await request<unknown>(`/mail/connections/${encodeURIComponent(connectionId)}/deactivate/`, { method: "POST", body: JSON.stringify({ expected_version: expectedVersion, confirm: true }), signal }, true);
    return mailConnectionResponse(responseData(payload));
  },

  async syncMailConnection(connectionId: string, signal?: AbortSignal) {
    const payload = await request<unknown>(`/mail/connections/${encodeURIComponent(connectionId)}/sync/`, { method: "POST", body: JSON.stringify({}), signal }, true);
    const value = responseData(payload);
    if (!isRecord(value) || typeof value.queued !== "boolean") throw invalidResponse();
    return value as { queued: boolean; task_id?: string };
  },

  async getMailSyncRun(connectionId: string, signal?: AbortSignal): Promise<MailSyncRun | null> {
    const payload = await request<unknown>(`/mail/connections/${encodeURIComponent(connectionId)}/sync-runs/latest/`, { method: "GET", signal });
    const value = responseData(payload);
    return value === null ? null : mailSyncResponse(value);
  },

  async listMailAccounts(connectionId?: string, signal?: AbortSignal): Promise<MailAccount[]> {
    const query = connectionId ? `?connection_id=${encodeURIComponent(connectionId)}` : "";
    const payload = await request<unknown>(`/mail/accounts/${query}`, { method: "GET", signal });
    return listData(payload, mailAccountResponse);
  },

  async listMailFolders(accountId?: string, signal?: AbortSignal): Promise<MailFolder[]> {
    const query = accountId ? `?account_id=${encodeURIComponent(accountId)}` : "";
    const payload = await request<unknown>(`/mail/folders/${query}`, { method: "GET", signal });
    return listData(payload, mailFolderResponse);
  },

  async listMailThreads(connectionId?: string, signal?: AbortSignal, filters?: { assigned_member_id?: string; routing_status?: string; binding?: "opportunity" | "project" | "unbound"; has_attachments?: boolean; received_from?: string; received_to?: string }): Promise<MailThread[]> {
    const params = new URLSearchParams();
    if (connectionId) params.set("connection_id", connectionId);
    if (filters?.assigned_member_id) params.set("assigned_member_id", filters.assigned_member_id);
    if (filters?.routing_status) params.set("routing_status", filters.routing_status);
    if (filters?.binding) params.set("binding", filters.binding);
    if (filters?.has_attachments !== undefined) params.set("has_attachments", String(filters.has_attachments));
    if (filters?.received_from) params.set("received_from", filters.received_from);
    if (filters?.received_to) params.set("received_to", filters.received_to);
    const query = params.toString() ? `?${params.toString()}` : "";
    const payload = await request<unknown>(`/mail/threads/${query}`, { method: "GET", signal });
    return listData(payload, mailThreadResponse);
  },

  async getMailThread(threadId: string, signal?: AbortSignal): Promise<MailThreadDetail> {
    const payload = await request<unknown>(`/mail/threads/${encodeURIComponent(threadId)}/`, { method: "GET", signal });
    const value = responseData(payload);
    if (!isRecord(value) || !isRecord(value.thread) || !Array.isArray(value.messages)) throw invalidResponse();
    return { thread: mailThreadResponse(value.thread), messages: value.messages.map(mailMessageResponse) };
  },

  async listMailConflicts(signal?: AbortSignal): Promise<MailRoutingConflict[]> {
    const payload = await request<unknown>("/mail/routing-conflicts/", { method: "GET", signal });
    return listData(payload, (value) => {
      if (!isRecord(value) || typeof value.id !== "string" || typeof value.thread_id !== "string" || typeof value.status !== "string") throw invalidResponse();
      return value as unknown as MailRoutingConflict;
    });
  },

  async resolveMailConflict(conflictId: string, input: { expected_version: number; resolution: string; member_id?: string; project_id?: string; command_id: string }, signal?: AbortSignal) {
    const payload = await request<unknown>(`/mail/routing-conflicts/${encodeURIComponent(conflictId)}/resolve/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload);
    if (!isRecord(value) || typeof value.id !== "string" || typeof value.status !== "string") throw invalidResponse();
    return value as { id: string; status: string; version: number };
  },

  async listMailRoutingRules(signal?: AbortSignal): Promise<MailRoutingRule[]> {
    const payload = await request<unknown>("/mail/routing-rules/", { method: "GET", signal });
    return listData(payload, (value) => {
      if (!isRecord(value) || typeof value.id !== "string" || typeof value.email !== "string" || typeof value.target_member_id !== "string") throw invalidResponse();
      return value as unknown as MailRoutingRule;
    });
  },

  async createMailRoutingRule(input: { normalized_recipient_email: string; target_member_id: string; room_id?: string | null; priority?: number }, signal?: AbortSignal) {
    const payload = await request<unknown>("/mail/routing-rules/", { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload);
    if (!isRecord(value) || typeof value.id !== "string") throw invalidResponse();
    return value as unknown as MailRoutingRule;
  },

  async updateMailRoutingRule(ruleId: string, input: { command_id: string; expected_version: number; active?: boolean; priority?: number; target_member_id?: string; room_id?: string | null }, signal?: AbortSignal) {
    const payload = await request<unknown>(`/mail/routing-rules/${encodeURIComponent(ruleId)}/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload);
    if (!isRecord(value) || typeof value.id !== "string") throw invalidResponse();
    return value as unknown as MailRoutingRule;
  },

  async bindMailThread(threadId: string, input: { expected_version: number; opportunity_id?: string | null; project_id?: string | null; reason?: string; command_id: string }, signal?: AbortSignal): Promise<MailThread> {
    const payload = await request<unknown>(`/mail/threads/${encodeURIComponent(threadId)}/binding/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return mailThreadResponse(responseData(payload));
  },

  async listMailDrafts(signal?: AbortSignal): Promise<MailDraft[]> {
    const payload = await request<unknown>("/mail/drafts/", { method: "GET", signal });
    return listData(payload, mailDraftResponse);
  },

  async createMailDraft(input: Record<string, unknown>, signal?: AbortSignal): Promise<{ value: MailDraft; replayed: boolean }> {
    const payload = await request<unknown>("/mail/drafts/", { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: mailDraftResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async reviseMailDraft(draftId: string, input: Record<string, unknown>, signal?: AbortSignal): Promise<{ value: MailDraft; replayed: boolean }> {
    const payload = await request<unknown>(`/mail/drafts/${encodeURIComponent(draftId)}/revisions/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: mailDraftResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async cancelMailDraft(draftId: string, input: { expected_revision: number; reason: string }, signal?: AbortSignal): Promise<MailDraft> {
    const payload = await request<unknown>(`/mail/drafts/${encodeURIComponent(draftId)}/cancel/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return mailDraftResponse(responseData(payload));
  },

  async listOutboundMail(status?: string, signal?: AbortSignal): Promise<OutboundMailIntent[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : "";
    const payload = await request<unknown>(`/mail/outbox/${query}`, { method: "GET", signal });
    return listData(payload, outboundIntentResponse);
  },

  async createOutboundMail(input: { draft_id: string; command_id: string; mail_type?: string; delivery_intent_id?: string | null }, signal?: AbortSignal): Promise<OutboundMailIntent> {
    const payload = await request<unknown>("/mail/outbox/", { method: "POST", body: JSON.stringify(input), signal }, true);
    return outboundIntentResponse(responseData(payload));
  },

  async getOutboundMail(intentId: string, signal?: AbortSignal): Promise<OutboundMailIntent> {
    const payload = await request<unknown>(`/mail/outbox/${encodeURIComponent(intentId)}/`, { method: "GET", signal });
    return outboundIntentResponse(responseData(payload));
  },

  async commandOutboundMail(intentId: string, action: string, input: Record<string, unknown>, signal?: AbortSignal): Promise<OutboundMailIntent> {
    const payload = await request<unknown>(`/mail/outbox/${encodeURIComponent(intentId)}/${encodeURIComponent(action)}/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload);
    if (isRecord(value) && isRecord(value.intent)) return outboundIntentResponse(value.intent);
    if (isRecord(value) && typeof value.status === "string") return outboundIntentResponse(value);
    throw invalidResponse();
  },

  async listOutboundAttempts(intentId: string, signal?: AbortSignal): Promise<OutboundMailAttempt[]> {
    const payload = await request<unknown>(`/mail/outbox/${encodeURIComponent(intentId)}/attempts/`, { method: "GET", signal });
    return listData(payload, outboundAttemptResponse);
  },

  async listSalesContacts(signal?: AbortSignal): Promise<SalesContact[]> {
    const payload = await request<unknown>("/sales/contacts/", { method: "GET", signal });
    return listData(payload, salesContactResponse);
  },

  async createSalesContact(input: Record<string, unknown>, signal?: AbortSignal): Promise<SalesContact> {
    const payload = await request<unknown>("/sales/contacts/", { method: "POST", body: JSON.stringify(input), signal }, true);
    return salesContactResponse(responseData(payload));
  },

  async salesContactCommand(contactId: string, action: string, input: Record<string, unknown>, signal?: AbortSignal): Promise<SalesContact> {
    const payload = await request<unknown>(`/sales/contacts/${encodeURIComponent(contactId)}/${encodeURIComponent(action)}/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return salesContactResponse(responseData(payload));
  },

  async listSalesEngagements(stage?: string, signal?: AbortSignal): Promise<SalesEngagement[]> {
    const query = stage ? `?stage=${encodeURIComponent(stage)}` : "";
    const payload = await request<unknown>(`/sales/engagements/${query}`, { method: "GET", signal });
    return listData(payload, salesEngagementResponse);
  },

  async getSalesEngagement(engagementId: string, signal?: AbortSignal): Promise<SalesEngagement> {
    const payload = await request<unknown>(`/sales/engagements/${encodeURIComponent(engagementId)}/`, { method: "GET", signal });
    return salesEngagementResponse(responseData(payload));
  },

  async salesEngagementCommand(engagementId: string, action: string, input: Record<string, unknown>, signal?: AbortSignal): Promise<SalesEngagement> {
    const payload = await request<unknown>(`/sales/engagements/${encodeURIComponent(engagementId)}/${encodeURIComponent(action)}/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload);
    if (isRecord(value) && isRecord(value.engagement)) return salesEngagementResponse(value.engagement);
    return salesEngagementResponse(value);
  },

  async listSalesReviews(signal?: AbortSignal): Promise<SalesReview[]> {
    const payload = await request<unknown>("/sales/responses/", { method: "GET", signal });
    return listData(payload, salesReviewResponse);
  },

  async listSalesFollowUps(signal?: AbortSignal): Promise<SalesFollowUp[]> {
    const payload = await request<unknown>("/sales/follow-ups/", { method: "GET", signal });
    return listData(payload, salesFollowUpResponse);
  },

  async createSalesFollowUp(input: Record<string, unknown>, signal?: AbortSignal): Promise<SalesFollowUp> {
    const payload = await request<unknown>("/sales/follow-ups/", { method: "POST", body: JSON.stringify(input), signal }, true);
    return salesFollowUpResponse(responseData(payload));
  },

  async commandSalesFollowUp(followUpId: string, action: string, input: Record<string, unknown>, signal?: AbortSignal): Promise<SalesFollowUp> {
    const payload = await request<unknown>(`/sales/follow-ups/${encodeURIComponent(followUpId)}/${encodeURIComponent(action)}/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return salesFollowUpResponse(responseData(payload));
  },

  async listWorkflows(signal?: AbortSignal): Promise<WorkflowRun[]> {
    const payload = await request<unknown>("/workflows/", { method: "GET", signal });
    return listData(payload, workflowResponse);
  },

  async createWorkflow(input: { project_id: string; template: string; command_id: string }, signal?: AbortSignal): Promise<{ value: WorkflowRun; replayed: boolean }> {
    const payload = await request<unknown>("/workflows/", { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: workflowResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async getWorkflow(workflowId: string, signal?: AbortSignal): Promise<WorkflowRun> {
    const payload = await request<unknown>(`/workflows/${encodeURIComponent(workflowId)}/`, { method: "GET", signal });
    return workflowResponse(responseData(payload));
  },

  async transitionWorkflow(workflowId: string, target: string, input: { command_id: string; expected_version: number }, signal?: AbortSignal): Promise<WorkflowRun> {
    const payload = await request<unknown>(`/workflows/${encodeURIComponent(workflowId)}/${encodeURIComponent(target)}/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return workflowResponse(responseData(payload));
  },

  async listHandoffs(signal?: AbortSignal): Promise<AgentHandoff[]> {
    const payload = await request<unknown>("/handoffs/", { method: "GET", signal });
    return listData(payload, handoffResponse);
  },

  async createHandoff(input: Record<string, unknown>, signal?: AbortSignal): Promise<AgentHandoff> {
    const payload = await request<unknown>("/handoffs/", { method: "POST", body: JSON.stringify(input), signal }, true);
    return handoffResponse(responseData(payload));
  },

  async transitionHandoff(handoffId: string, target: string, input: { command_id: string; expected_version: number }, signal?: AbortSignal): Promise<AgentHandoff> {
    const payload = await request<unknown>(`/handoffs/${encodeURIComponent(handoffId)}/${encodeURIComponent(target)}/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return handoffResponse(responseData(payload));
  },

  async listMeetings(signal?: AbortSignal): Promise<Meeting[]> {
    const payload = await request<unknown>("/meetings/", { method: "GET", signal });
    return listData(payload, meetingResponse);
  },

  async createMeeting(input: Record<string, unknown>, signal?: AbortSignal): Promise<Meeting> {
    const payload = await request<unknown>("/meetings/", { method: "POST", body: JSON.stringify(input), signal }, true);
    return meetingResponse(responseData(payload));
  },

  async getMeeting(meetingId: string, signal?: AbortSignal): Promise<Meeting> {
    const payload = await request<unknown>(`/meetings/${encodeURIComponent(meetingId)}/`, { method: "GET", signal });
    return meetingResponse(responseData(payload));
  },

  async transitionMeeting(meetingId: string, target: string, input: { command_id: string; expected_version: number }, signal?: AbortSignal): Promise<Meeting> {
    const payload = await request<unknown>(`/meetings/${encodeURIComponent(meetingId)}/${encodeURIComponent(target)}/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return meetingResponse(responseData(payload));
  },

  async addMeetingContribution(meetingId: string, input: Record<string, unknown>, signal?: AbortSignal): Promise<MeetingContribution> {
    const payload = await request<unknown>(`/meetings/${encodeURIComponent(meetingId)}/contributions/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload);
    return collaborationRecord<MeetingContribution>(value, ["id", "meeting_id", "participant_id", "canonical_hash"]);
  },

  async getMeetingMinutes(meetingId: string, signal?: AbortSignal): Promise<MeetingMinutes> {
    const payload = await request<unknown>(`/meetings/${encodeURIComponent(meetingId)}/minutes/`, { method: "GET", signal });
    return collaborationRecord<MeetingMinutes>(responseData(payload), ["id", "meeting_id", "canonical_hash"]);
  },

  async listDailyReports(date?: string, signal?: AbortSignal): Promise<DailyAgentReport[]> {
    const query = date ? `?date=${encodeURIComponent(date)}` : "";
    const payload = await request<unknown>(`/reports/daily/${query}`, { method: "GET", signal });
    return listData(payload, dailyReportResponse);
  },

  async generateDailyReports(date?: string, signal?: AbortSignal): Promise<DailyAgentReport[]> {
    const payload = await request<unknown>("/reports/daily/", { method: "POST", body: JSON.stringify(date ? { date } : {}), signal }, true);
    return listData(payload, dailyReportResponse);
  },

  async getActionCenter(signal?: AbortSignal): Promise<CollaborationActionCenter> {
    const payload = await request<unknown>("/action-center/", { method: "GET", signal });
    const value = responseData(payload);
    if (!isRecord(value)) throw invalidResponse();
    return value as unknown as CollaborationActionCenter;
  },

  async listReleaseCandidates(filters?: { project_id?: string; status?: string }, signal?: AbortSignal): Promise<ReleaseCandidate[]> {
    const params = new URLSearchParams(); if (filters?.project_id) params.set("project_id", filters.project_id); if (filters?.status) params.set("status", filters.status);
    const payload = await request<unknown>(`/release-candidates/${params.toString() ? `?${params.toString()}` : ""}`, { method: "GET", signal });
    return listData(payload, releaseCandidateResponse);
  },

  async createReleaseCandidate(input: Record<string, unknown>, signal?: AbortSignal): Promise<{ value: ReleaseCandidate; replayed: boolean }> {
    const payload = await request<unknown>("/release-candidates/", { method: "POST", body: JSON.stringify(input), signal }, true);
    return { value: releaseCandidateResponse(responseData(payload)), replayed: isRecord(payload) && isRecord(payload.meta) && payload.meta.idempotent_replay === true };
  },

  async getReleaseCandidate(candidateId: string, signal?: AbortSignal): Promise<ReleaseCandidate> {
    const payload = await request<unknown>(`/release-candidates/${encodeURIComponent(candidateId)}/`, { method: "GET", signal });
    return releaseCandidateResponse(responseData(payload));
  },

  async reviseReleaseCandidate(candidateId: string, input: Record<string, unknown>, signal?: AbortSignal): Promise<ReleaseCandidate> {
    const payload = await request<unknown>(`/release-candidates/${encodeURIComponent(candidateId)}/revisions/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return releaseCandidateResponse(responseData(payload));
  },

  async commandReleaseCandidate(candidateId: string, action: "submit" | "request-changes" | "approve" | "cancel", input: Record<string, unknown>, signal?: AbortSignal): Promise<ReleaseCandidate> {
    const payload = await request<unknown>(`/release-candidates/${encodeURIComponent(candidateId)}/${action}/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return releaseCandidateResponse(responseData(payload));
  },

  async listDeliveryPackages(projectId?: string, signal?: AbortSignal): Promise<DeliveryPackage[]> {
    const query = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
    const payload = await request<unknown>(`/delivery-packages/${query}`, { method: "GET", signal });
    return listData(payload, deliveryPackageResponse);
  },

  async createDeliveryPackage(input: { candidate_id: string; command_id: string }, signal?: AbortSignal): Promise<DeliveryPackage> {
    const payload = await request<unknown>("/delivery-packages/", { method: "POST", body: JSON.stringify(input), signal }, true);
    return deliveryPackageResponse(responseData(payload));
  },

  async getDeliveryPackage(packageId: string, signal?: AbortSignal): Promise<DeliveryPackage> {
    const payload = await request<unknown>(`/delivery-packages/${encodeURIComponent(packageId)}/`, { method: "GET", signal });
    return deliveryPackageResponse(responseData(payload));
  },

  async reviseDeliveryPackage(packageId: string, input: Record<string, unknown>, signal?: AbortSignal): Promise<DeliveryPackage> {
    const payload = await request<unknown>(`/delivery-packages/${encodeURIComponent(packageId)}/revisions/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return deliveryPackageResponse(responseData(payload));
  },

  async validateDeliveryPackage(packageId: string, input: { expected_version: number; command_id: string }, signal?: AbortSignal): Promise<DeliveryPackage> {
    const payload = await request<unknown>(`/delivery-packages/${encodeURIComponent(packageId)}/validate/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    return deliveryPackageResponse(responseData(payload));
  },

  async requestCustomerDelivery(packageId: string, input: { expected_version: number; command_id: string; reason: string }, signal?: AbortSignal) {
    const payload = await request<unknown>(`/delivery-packages/${encodeURIComponent(packageId)}/request-approval/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload); if (!isRecord(value) || typeof value.approval_request_id !== "string" || !isRecord(value.intent)) throw invalidResponse();
    return value as { approval_request_id: string; project_id: string; target_revision_id: string; target_hash: string; intent: DeliveryIntent; sent: false; simulation: true };
  },

  async approveCustomerDelivery(packageId: string, input: { expected_version: number; command_id: string; decision: "APPROVE" | "REJECT"; reason: string; expected_request_version: number }, signal?: AbortSignal) {
    const payload = await request<unknown>(`/delivery-packages/${encodeURIComponent(packageId)}/approve/`, { method: "POST", body: JSON.stringify(input), signal }, true);
    const value = responseData(payload); if (!isRecord(value) || typeof value.approval_request_id !== "string" || !isRecord(value.intent)) throw invalidResponse();
    return value as { approval_request_id: string; status: string; intent: DeliveryIntent; sent: false; simulation: true };
  },

  async listDeliveryIntents(signal?: AbortSignal): Promise<DeliveryIntent[]> {
    const payload = await request<unknown>("/delivery-intents/", { method: "GET", signal }); return listData(payload, deliveryIntentResponse);
  },

  async createDeliveryIntent(input: { package_id: string; command_id: string; subject_template: string; body_template: string }, signal?: AbortSignal): Promise<DeliveryIntent> {
    const payload = await request<unknown>("/delivery-intents/", { method: "POST", body: JSON.stringify(input), signal }, true); return deliveryIntentResponse(responseData(payload));
  },

  async getDeliveryIntent(intentId: string, signal?: AbortSignal): Promise<DeliveryIntent> {
    const payload = await request<unknown>(`/delivery-intents/${encodeURIComponent(intentId)}/`, { method: "GET", signal }); return deliveryIntentResponse(responseData(payload));
  },

  async getDeliveryCenter(signal?: AbortSignal): Promise<DeliveryCenterData> {
    const payload = await request<unknown>("/delivery-center/", { method: "GET", signal }); const value = responseData(payload); if (!isRecord(value)) throw invalidResponse(); return value as unknown as DeliveryCenterData;
  },

  newClientMessageId: randomId,
  newCommandId: randomId,
};
